import { apiFetch } from "../api/client";
import type { Extended } from "../settings/types";

/**
 * WHICH EVENT ARE WE ON — resolved from ONE segment of the URL.
 *
 * `/:context/...`, and `GET /context/:context` turns that segment into the event
 * and the domain it belongs to. The client never has to know the pair, and never
 * has to be told it twice.
 *
 * ⚠️ ONE OPAQUE TOKEN, not `domainId/eventId` spelled out, and that is not a
 * detail: every invitation link, QR code and email already in circulation
 * carries this shape. Changing it would break everything already sent.
 *
 * Rejected alternative — a subdomain per event. Prettier, but it needs DNS work
 * for every event, it breaks on the CloudFront preview domain, and it is the same
 * guess-from-the-hostname that V1 makes for its API address and that this project
 * deliberately removed.
 *
 * Rejected too — remembering the context in storage as the source of truth. Two
 * events open in two tabs would then fight over one value, and the loser shows a
 * participant somebody else's event. Storage cannot arbitrate; the URL can.
 */

export type EventContext = {
  eventId: number;
  domainId: number;
  /** The token as it appeared in the URL — links are rebuilt from it. */
  token: string;
  /**
   * ═══ CE QUE L'ÉVÉNEMENT SAIT DE LUI-MÊME ═══
   *
   * Son nom et ses jours : des colonnes de la table `Events`, pas des réglages du
   * front-office. Le nom sert de repli quand l'organisateur n'en a pas écrit (voir
   * `settings/eventName.ts`), les jours au badge et à l'étape dates.
   *
   * Facultatifs : un vieil événement peut n'avoir ni nom ni jours déclarés.
   */
  name?: string;
  /**
   * Les jours, dans l'ordre où la base les rend.
   *
   * ⚠️ AVEC LEUR IDENTIFIANT, ET PAS SEULEMENT LEUR DATE. `EventDays.id` est ce qui
   * s'écrit dans `UserEventDays` : l'étape dates choisit des jours, pas des chaînes.
   * La date seule suffit au badge, qui n'en tire qu'une période à afficher ; elle ne
   * suffit pas à inscrire quelqu'un.
   */
  days?: ReadonlyArray<{ id: number; day: string }>;
  /**
   * Every language's raw `extended`, keyed by language KEY. It rides along with
   * the context — one public call for the event AND its whole configuration.
   */
  settingsAll: Record<string, Extended | null>;
};

/** Told apart from a network failure: an unknown event is not an outage. */
export class UnknownContext extends Error {
  constructor(readonly token: string) {
    super(`Aucun événement ne correspond à « ${token} ».`);
    this.name = "UnknownContext";
  }
}

export async function resolveContext(
  token: string,
  signal?: AbortSignal,
): Promise<EventContext> {
  const answer = await apiFetch<{
    id?: number;
    domainId?: number;
    name?: string;
    days?: ReadonlyArray<{ id?: number; day?: string }>;
    settingsAll?: Record<string, Extended | null>;
  }>(`/context/${encodeURIComponent(token)}`, { signal });

  // A 200 carrying neither id would otherwise leave the application running with
  // `undefined` for an event, and every later call failing for no visible reason.
  if (!answer?.id || !answer.domainId) throw new UnknownContext(token);

  return {
    eventId: answer.id,
    domainId: answer.domainId,
    token,
    ...(answer.name ? { name: answer.name } : {}),
    // Une journée sans date n'est pas une journée : elle ne compte pas dans la
    // période, et la laisser passer produirait « Invalid Date » sur le badge.
    // Sans identifiant non plus : on ne pourrait ni la proposer ni l'inscrire.
    ...(answer.days
      ? {
          days: answer.days.flatMap((d) =>
            typeof d?.id === "number" && typeof d?.day === "string" && d.day
              ? [{ id: d.id, day: d.day }]
              : [],
          ),
        }
      : {}),
    settingsAll: answer.settingsAll ?? {},
  };
}
